import React from 'react';
import { useAuth } from '@kuow/shared-ui';
import { getConfig, getCoveSupportConfig, isFatalCoveBootError, loadCoveEmbed, logger } from '@kuow/core';
import type { CoveWidgetError, CoveWidgetInstance } from '@kuow/core';
import { createToaster, Toast, Toaster } from '@chakra-ui/react';

export type CoveSupportStatus = 'off' | 'idle' | 'loading' | 'ready' | 'unavailable';

export interface CoveSupportContextValue {
  /** False when the feature is unconfigured or has failed for this page. */
  available: boolean;
  /** True while the embed script is loading after the first open. */
  opening: boolean;
  open: () => void;
}

export const CoveSupportContext = React.createContext<CoveSupportContextValue | undefined>(undefined);

export const SUPPORT_UNAVAILABLE_MESSAGE = "Support chat isn't available right now.";

const toaster = createToaster({ placement: 'top' });

/**
 * Owns the one Cove widget instance for the portal.
 *
 * Cove's init() is a singleton per loaded bundle, and its floating panel
 * persists a conversation per tab, so this must be mounted exactly once, above
 * the router outlet, and never unmount on navigation. The instance is created
 * lazily on the first open() — nothing is loaded for visitors who never ask
 * for help — and destroyed only when the provider unmounts.
 */
export const CoveSupportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = useAuth();
  const currentToken = token?.token ?? null;

  const [config] = React.useState(() => getCoveSupportConfig());
  const [status, setStatus] = React.useState<CoveSupportStatus>(() => (config ? 'idle' : 'off'));

  const instanceRef = React.useRef<CoveWidgetInstance | null>(null);
  // Latest token, readable from open()'s async continuation without a stale closure.
  const tokenRef = React.useRef<string | null>(currentToken);
  const disposedRef = React.useRef(false);
  const unavailableRef = React.useRef(false);

  // A half-configured environment (one of the pair set) is a mistake worth one
  // warning; a fully blank pair is the intended "off" state and stays silent.
  React.useEffect(() => {
    if (config) return;
    const { coveEmbedUrl, coveWidgetId } = getConfig();
    if (coveEmbedUrl.trim() !== '' || coveWidgetId.trim() !== '') {
      logger.warn('Member Portal: Cove support is half-configured; set both coveEmbedUrl and coveWidgetId', {
        hasEmbedUrl: coveEmbedUrl.trim() !== '',
        hasWidgetId: coveWidgetId.trim() !== '',
      });
    }
  }, [config]);

  const markUnavailable = React.useCallback((message: string, data: Record<string, unknown>) => {
    if (unavailableRef.current) return;
    unavailableRef.current = true;
    logger.error(message, data);
    if (disposedRef.current) return;
    setStatus('unavailable');
    toaster.create({ title: SUPPORT_UNAVAILABLE_MESSAGE, type: 'error' });
  }, []);

  const handleCoveError = React.useCallback(
    (error: CoveWidgetError) => {
      if (isFatalCoveBootError(error)) {
        markUnavailable('Member Portal: Cove support widget failed to boot', { ...error });
        return;
      }
      // Runtime codes (REHYDRATE_FAILED, AUTHENTICATE_FAILED, …): Cove shows its
      // own in-panel state; we only keep a record.
      logger.warn('Member Portal: Cove support widget reported an error', { ...error });
    },
    [markUnavailable],
  );

  const open = React.useCallback(() => {
    if (unavailableRef.current || !config) return;

    const existing = instanceRef.current;
    if (existing) {
      existing.open();
      return;
    }
    if (status !== 'idle') return;

    setStatus('loading');
    void loadCoveEmbed(config.embedUrl)
      .then((cove) => {
        if (disposedRef.current || unavailableRef.current) return;

        const created = cove.init({
          widgetId: config.widgetId,
          hidden: true,
          persistSession: true,
          onError: handleCoveError,
        });
        // init() can report a fatal error synchronously (e.g. MISSING_API_URL)
        // and hand back a no-op instance; don't keep it.
        if (unavailableRef.current) {
          created.destroy();
          return;
        }

        // Same tick as init(): Cove reads the token one microtask later for a
        // rehydrated conversation's first request.
        if (!created.authenticate(tokenRef.current)) {
          logger.warn('Member Portal: Cove rejected the initial identity');
        }
        created.open();
        instanceRef.current = created;
        setStatus('ready');
      })
      .catch((error: unknown) => {
        markUnavailable('Member Portal: could not start the Cove support widget', { error });
      });
  }, [config, status, handleCoveError, markUnavailable]);

  // Mirror every auth transition into Cove. Before the first open there is no
  // instance and open() applies tokenRef itself; once an instance exists, a new
  // token is asserted on the live conversation (sign-in mid-chat just works),
  // and a null token is retracted *and* the conversation is closed —
  // authenticate(null) alone leaves the previous member's transcript on screen
  // and in sessionStorage for whoever uses the tab next.
  //
  // The gate is the instance, not `status`: `status` here is a render-time
  // closure and can lag the `.then` in open() that sets both instanceRef and
  // `ready` in one synchronous block. A token change that commits in that gap
  // would otherwise be skipped and never replayed. instanceRef is written in
  // the same block as the initial authenticate, so it is the right source of
  // truth — and it also lets sign-out still retract identity on an instance
  // that Cove marked unavailable after boot.
  React.useEffect(() => {
    const previous = tokenRef.current;
    tokenRef.current = currentToken;
    if (previous === currentToken) return;

    const instance = instanceRef.current;
    if (!instance) return;

    if (!instance.authenticate(currentToken)) {
      logger.warn('Member Portal: Cove rejected the identity update');
      return;
    }
    if (currentToken === null) instance.startOver();
  }, [currentToken]);

  React.useEffect(() => {
    // StrictMode runs this effect twice in dev; reset on each mount.
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
      instanceRef.current?.destroy();
      instanceRef.current = null;
    };
  }, []);

  const value = React.useMemo<CoveSupportContextValue>(
    () => ({
      available: status === 'idle' || status === 'loading' || status === 'ready',
      opening: status === 'loading',
      open,
    }),
    [status, open],
  );

  return (
    <CoveSupportContext.Provider value={value}>
      {children}
      <Toaster toaster={toaster}>
        {(toast) => (
          <Toast.Root>
            <Toast.Title>{toast.title}</Toast.Title>
            <Toast.CloseTrigger />
          </Toast.Root>
        )}
      </Toaster>
    </CoveSupportContext.Provider>
  );
};
